import { computed, readonly, ref, shallowRef } from 'vue';
import type { CommunityUser, LiveMember, LiveSnapshot, Party } from '@garmoth/community-contracts';
import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import { connectLiveRoster, type LiveStatus } from '../api/live';
import { useSession } from './session';

/**
 * The visitor's own party, its membership, and the live roster the desktop
 * apps push. One store for the whole site: the sticky party strip in the shell
 * and the party page read the same snapshot, so only one socket is open.
 */

/** A snapshot older than this is shown as stale instead of live. */
const STALE_AFTER_MS = 90_000;

export type PartyRowState = 'grinding' | 'paused' | 'stale' | 'idle';

export interface PartyRow {
  user: CommunityUser;
  snapshot: LiveSnapshot | null;
  updatedAt: string | null;
  state: PartyRowState;
  isOwner: boolean;
  isSelf: boolean;
}

const party = ref<Party | null>(null);
const roster = shallowRef<LiveMember[]>([]);
const liveStatus = ref<LiveStatus>('closed');
const liveError = ref<string | null>(null);
const loading = ref(false);
const loaded = ref(false);
const error = ref<string | null>(null);
/** Re-read on every roster message so relative ages stay honest. */
const now = ref(Date.now());

const session = useSession();
let closeLive: (() => void) | undefined;
let ageTimer: ReturnType<typeof setInterval> | undefined;

async function refresh(): Promise<void> {
  if (loading.value) return;
  loading.value = true;
  try {
    party.value = (await api.activeParty()).party;
    error.value = null;
    loaded.value = true;
  } catch (cause) {
    if (cause instanceof ApiError && cause.isUnauthorized) {
      party.value = null;
      loaded.value = true;
      error.value = null;
    } else error.value = cause instanceof ApiError ? cause.message : 'The party could not be loaded.';
  } finally {
    loading.value = false;
  }
}

/**
 * Opens the roster socket. The app shell is the only owner: it starts the
 * socket when a party appears and stops it when the party or the session ends,
 * so no route mounts a second connection.
 */
function startLive() {
  if (closeLive || !party.value) return;
  closeLive = connectLiveRoster(party.value.id, {
    roster: members => {
      roster.value = members;
      now.value = Date.now();
    },
    status: status => {
      liveStatus.value = status;
    },
    error: message => {
      liveError.value = message;
    },
  });
  // A member that stops reporting sends no message, so age has to be re-read.
  ageTimer = setInterval(() => {
    now.value = Date.now();
  }, 15_000);
}

function stopLive() {
  closeLive?.();
  closeLive = undefined;
  clearInterval(ageTimer);
  ageTimer = undefined;
  roster.value = [];
  liveStatus.value = 'closed';
}

function rowState(member: LiveMember, reference: number): PartyRowState {
  if (!member.snapshot || !member.updatedAt) return 'idle';
  const age = reference - new Date(member.updatedAt).getTime();
  if (Number.isNaN(age) || age > STALE_AFTER_MS) return 'stale';
  return member.snapshot.isRunning ? 'grinding' : 'paused';
}

/**
 * Rows are the union of the live roster and the party membership the REST
 * route returned. The roster leads: it is the fresher list, so a member who
 * joined since the last refresh already has a row.
 */
const rows = computed<PartyRow[]>(() => {
  const current = party.value;
  if (!current) return [];
  const live = new Map(roster.value.map(member => [member.user.id, member]));
  const members = [...roster.value.map(member => member.user), ...current.members.filter(member => !live.has(member.id))];
  const reference = Math.max(now.value, Date.now());
  return members.map(member => {
    const snapshot = live.get(member.id);
    return {
      user: member,
      snapshot: snapshot?.snapshot ?? null,
      updatedAt: snapshot?.updatedAt ?? null,
      state: snapshot ? rowState(snapshot, reference) : 'idle',
      isOwner: member.id === current.ownerId,
      isSelf: member.id === session.user.value?.id,
    };
  });
});

const totals = computed(() => {
  const active = rows.value.filter(row => row.snapshot && row.state !== 'stale');
  return {
    silver: active.reduce((sum, row) => sum + (row.snapshot?.totalSilver ?? 0), 0),
    silverPerHour: active.reduce((sum, row) => sum + (row.snapshot?.silverPerHour ?? 0), 0),
    deaths: active.reduce((sum, row) => sum + (row.snapshot?.deathCount ?? 0), 0),
    grinding: rows.value.filter(row => row.state === 'grinding').length,
  };
});

async function create() {
  party.value = await api.createParty();
  loaded.value = true;
}

async function join(inviteCode: string) {
  party.value = await api.joinParty(inviteCode);
  loaded.value = true;
}

async function leave() {
  if (!party.value) return;
  await api.leaveParty(party.value.id);
  party.value = null;
  stopLive();
}

/** Drops local state on sign-out so the next visitor never sees this party. */
function clear() {
  party.value = null;
  loaded.value = false;
  error.value = null;
  stopLive();
}

export function useParty() {
  return {
    party: readonly(party),
    rows,
    totals,
    loading: readonly(loading),
    loaded: readonly(loaded),
    error: readonly(error),
    liveStatus: readonly(liveStatus),
    liveError: readonly(liveError),
    refresh,
    startLive,
    stopLive,
    create,
    join,
    leave,
    clear,
  };
}
